import {
  useEffect,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type ReactNode
} from 'react';

type AnimatedSectionProps = {
  children: ReactNode;
} & ComponentPropsWithoutRef<'section'>;

export function AnimatedSection({ children, className = '', ...sectionProps }: AnimatedSectionProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const canAnimate = typeof window.matchMedia === 'function'
    && typeof IntersectionObserver !== 'undefined'
    && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const [visible, setVisible] = useState(!canAnimate);

  useEffect(() => {
    if (!canAnimate || !sectionRef.current) return;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisible(true);
        observer.disconnect();
      }
    }, { threshold: 0.15 });

    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [canAnimate]);

  return (
    <section
      ref={sectionRef}
      className={`reveal ${className}`.trim()}
      data-visible={visible}
      {...sectionProps}
    >
      {children}
    </section>
  );
}
